<template>
  <div>
    <h3>Media Assets</h3>
    <p class="">
      Easily upload and crop your campaign assets;<br>
      <a
        class="guide-link"
        href="https://a.smartology.co/smartmatch-best-practice-content.pdf"
        target="_blank"
      >
        view our detailed Best Practice Guide
      </a> for more information.
    </p>
    <div class="m-2">
      <template
        v-if="!isCroppingImage"
      >
        <b-button
          variant="primary"
          class="w-100"
          @click="openUploadImageModal()"
          :disabled="GET_IS_CAMPAIGN_READONLY"
        >
          Upload file
          <i class="fal fa-cloud-upload" />
        </b-button>
      </template>
    </div>
    <div class="mt-4 ml-2">
      <h5 class="mb-2">
        Current Asset
      </h5>
      <current-asset
        :aspect-ratio="getAspectRatio()"
        :image-path="currentAssetLink"
      />
    </div>
    <div
      class="mt-4 ml-2"
      v-if="recentAssetNames.length > 0"
    >
      <h5 class="mb-2">
        Recent Assets
      </h5>
      <div class="m-2">
        <b-row>
          <b-col
            class="col-6"
            :class="`${index % 2 !== 0 ? 'pr-0 pl-1' : 'pr-1 pl-0'}`"
            v-for="(assetName, index) in recentAssetNames"
            :key="assetName"
          >
            <recent-asset
              :image-name="assetName"
              :aspect-ratio="getAspectRatio()"
              :creative-size-name="selectedCreativeSizeName"
              :add-right-padding="index % 2 === 0"
              :selected="assetName === currentAssetFileName"
              @swap="swapImage"
            />
          </b-col>
        </b-row>
      </div>
    </div>

    <upload-image-modal
      :custom-props="UploadImageModalProps"
      @uploadImage="uploadImage(...arguments)"
      @closeUploadImageModal="closeUploadImageModal"
    />
  </div>
</template>
<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import * as campaignTypes from '@/modules/campaigns/store/types';
import * as contentTypes from '@/modules/content/store/types';


import UploadImageModal from '@/modules/content/components/CreativesPage/Sidebar/UploadImageModal.vue';
import CurrentAsset from '@/modules/content/components/CreativesPage/Sidebar/MediaAssets/CurrentAsset.vue';
import RecentAsset from '@/modules/content/components/CreativesPage/Sidebar/MediaAssets/RecentAsset.vue';

export default {
  name: 'MediaAssets',
  components: {
    RecentAsset,
    CurrentAsset,
    UploadImageModal,
  },
  data() {
    return {
      isCroppingImage: false,
    };
  },
  computed: {
    ...mapState('content', [
      'feedId',
      'imageAssetNames',
      'selectedArticleUuid',
      'selectedCreativeId',
      'selectedCreativeSizeName',
      'creativeTypeSelected',
    ]),
    ...mapGetters('content', [
      contentTypes.GET_CONTENT_ITEM_DISPLAY_PROPERTIES,
    ]),
    ...mapGetters('campaigns', [
      campaignTypes.GET_IS_CAMPAIGN_READONLY,
    ]),
    imageDisplayPropertiesName() {
      return this.creativeTypeSelected === 'IMAGE' ? 'creativeImage' : 'imageFileName';
    },
    imageType() {
      return this.creativeTypeSelected === 'IMAGE' ? 'CREATIVE' : 'IMAGE';
    },
    recentAssetNames() {
      return this.imageAssetNames[this.selectedCreativeSizeName] || [];
    },
    currentAssetLink() {
      return this.GET_CONTENT_ITEM_DISPLAY_PROPERTIES(this.selectedArticleUuid, this.selectedCreativeId)?.[this.imageDisplayPropertiesName];
    },
    currentAssetFileName() {
      if (!this.currentAssetLink) return '';
      return this.currentAssetLink.split('/').pop();
    },
    UploadImageModalProps() {
      return {
        title: 'Upload Display Image',
        imageURL: this.currentAssetLink,
        supportedTypes: ['gif', 'jpg', 'jpeg', 'png'],
        maxSize: {
          size: '10',
          unit: 'mb',
        },
        maxSizeGIF: {
          size: '5',
          unit: 'mb',
        },
      };
    },
  },
  methods: {
    ...mapActions('content', [
      contentTypes.SET_IS_SIDEBAR_INPUT_FOCUSED,
      contentTypes.UPLOAD_CREATIVE_DISPLAY_IMAGE,
      contentTypes.TRIGGER_CREATIVE_RERENDER,
      contentTypes.SWAP_CREATIVE_DISPLAY_IMAGE,
      contentTypes.UPDATE_CONTENT_DISPLAY_COPY,
    ]),
    getAspectRatio() {
      const iFrameImageContainer = document.getElementById(`preview-iframe-${this.selectedCreativeId}`).contentDocument.getElementById('smartmatch-image');
      if (iFrameImageContainer) {
        const iFrameImageContainerComputedStyle = window.getComputedStyle(iFrameImageContainer);
        const marginY = parseInt(iFrameImageContainerComputedStyle['margin-top'], 10) + parseInt(iFrameImageContainerComputedStyle['margin-bottom'], 10);
        const marginX = parseInt(iFrameImageContainerComputedStyle['margin-left'], 10) + parseInt(iFrameImageContainerComputedStyle['margin-right'], 10);
        const height = parseInt(iFrameImageContainerComputedStyle.height, 10) - marginY;
        const width = parseInt(iFrameImageContainerComputedStyle.width, 10) - marginX;
        return width / height;
      }
      return 16 / 9;
    },
    async uploadImage(payload) {
      if (payload.type === 'url') {
        const requestPayload = {
          creativeId: this.selectedCreativeId,
          feedId: this.feedId,
          id: this.selectedArticleUuid,
          body: {
            [this.imageDisplayPropertiesName]: payload.value,
          },
        };
        await this.UPDATE_CONTENT_DISPLAY_COPY(requestPayload);
      } else if (payload.type === 'file') {
        payload.value.formData.append('body', new Blob([JSON.stringify({
          contentItemUuid: this.selectedArticleUuid,
          imageType: this.imageType,
        })], { type: 'application/json' }));
        const requestPayload = {
          creativeId: this.selectedCreativeId,
          formData: payload.value.formData,
        };
        await this.UPLOAD_CREATIVE_DISPLAY_IMAGE(requestPayload);
      }
      this.closeUploadImageModal();
    },
    openUploadImageModal() {
      if (this.GET_IS_CAMPAIGN_READONLY) return;
      this.SET_IS_SIDEBAR_INPUT_FOCUSED(true);
      this.$root.$emit('bv::show::modal', 'upload-image-modal');
    },
    closeUploadImageModal() {
      this.SET_IS_SIDEBAR_INPUT_FOCUSED(false);
      this.$root.$emit('bv::hide::modal', 'upload-image-modal');
    },
    swapImage(imageName) {
      this.SWAP_CREATIVE_DISPLAY_IMAGE({
        creativeId: this.selectedCreativeId,
        body: {
          imageType: this.imageType,
          contentItemUuid: this.selectedArticleUuid,
          fileName: imageName,
        },
      }).then(() => {
        this.TRIGGER_CREATIVE_RERENDER();
      });
    },
  },
};
</script>
<style scoped lang="scss">
.guide-link {
  color: #47474E;
  font-size: 1rem;
  text-decoration: underline;
}
</style>
